import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Mail, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.replace("Z", "+00:00"));
    if (isNaN(d.getTime())) return iso;
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function Inbox({ onChanged }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/inbox");
      setItems(data);
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const reprocess = async (emailId) => {
    setBusy(emailId);
    try {
      const { data } = await api.post(`/reprocess/${emailId}`);
      if (data?.queued) {
        toast("Re-extracting…", { description: "This can take up to a minute for long e-tickets." });
        // Poll the inbox to catch the background task's completion.
        // Refreshes at 3s, 8s, 18s, 45s. On each refresh, if the row's
        // reprocess_status flips to 'done' or 'error', show a final toast.
        const pollOnce = async (delayMs) => new Promise((resolve) => {
          setTimeout(async () => {
            try {
              const { data: fresh } = await api.get("/inbox");
              setItems(fresh);
              onChanged?.();
              const row = fresh.find(r => r.email_id === emailId);
              resolve(row);
            } catch {
              resolve(null);
            }
          }, delayMs);
        });
        for (const delay of [3000, 5000, 10000, 27000]) {
          const row = await pollOnce(delay);
          const status = row?.reprocess_status;
          if (status === "done") {
            const n = row.reservations_extracted || 0;
            toast.success(`Re-extracted ${n} reservation${n !== 1 ? "s" : ""}`);
            return;
          }
          if (status === "error") {
            const err = row.reprocess_error || "unknown";
            if (err === "empty_content") {
              toast.error("The email body was empty — nothing to extract");
            } else if (err === "timeout") {
              toast.error("Extraction took too long — please try Re-extract again");
            } else if (err.startsWith("quota:")) {
              toast.error("Daily extraction limit reached — try again tomorrow");
            } else if (err.startsWith("fetch_failed:404")) {
              toast.error("This email is no longer stored by Resend. Please forward the original confirmation again to your travel address.");
            } else if (err.startsWith("fetch_failed:401") || err.startsWith("fetch_failed:403")) {
              toast.error("Resend authentication issue — please contact support");
            } else if (err.startsWith("fetch_failed:429")) {
              toast.error("Rate-limited by Resend — please try again in a minute");
            } else if (err.startsWith("fetch_failed:5")) {
              toast.error("Resend is having temporary issues — please try again shortly");
            } else if (err.startsWith("fetch_failed:")) {
              toast.error(`Couldn't reach Resend (${err.split(":")[1]}) — please try Re-extract again`);
            } else if (err.startsWith("llm_error:")) {
              toast.error("The extractor hit an error — please try Re-extract again");
            } else {
              toast.error(`Re-extract failed: ${err}`);
            }
            return;
          }
        }
        toast("Still working…", { description: "Refresh in a moment to see the result." });
      } else if (data?.error === "timeout") {
        toast.error("Extraction took too long — please try again in a moment");
        await load(); onChanged?.();
      } else if (data?.error) {
        toast.error(`Re-extract failed: ${data.error}`);
        await load(); onChanged?.();
      } else {
        toast.success(`Re-extracted ${data.extracted} reservation${data.extracted !== 1 ? "s" : ""}`);
        await load(); onChanged?.();
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 502 || status === 504 || status === 524) {
        toast.error("The extraction service was slow — please try Re-extract again");
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "Re-extract failed");
      }
    } finally {
      setBusy(null);
    }
  };

  if (items === null) return null;
  if (items.length === 0) return null;

  return (
    <div className="rounded-card border border-hairline bg-surface" data-testid="inbox-panel">
      <div className="px-6 py-4 border-b border-sand-200 flex items-center justify-between">
        <p className="label-tag">Recent inbox</p>
        <button
          onClick={load}
          className="text-sand-500 hover:text-sand-900 transition-colors"
          title="Refresh"
          data-testid="inbox-refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      <ul className="divide-y divide-sand-100">
        {items.map((it) => {
          const ok = (it.reservations_extracted || 0) > 0;
          const queued = it.reprocess_status === "queued";
          const errored = it.reprocess_status === "error";
          const errCode = it.reprocess_error || "";
          const errText = queued
            ? "Re-extracting…"
            : errored
              ? (errCode.startsWith("fetch_failed:404")
                  ? "Email no longer available in Resend — please forward it again"
                  : errCode.startsWith("fetch_failed:401") || errCode.startsWith("fetch_failed:403")
                    ? "Resend authentication issue"
                    : errCode.startsWith("fetch_failed:429")
                      ? "Rate-limited by Resend — try again in a minute"
                      : errCode.startsWith("fetch_failed:5")
                        ? "Resend is having issues — try again shortly"
                        : errCode.startsWith("fetch_failed:")
                          ? `Couldn't reach Resend (${errCode.split(":")[1]}) — try again`
                          : errCode === "timeout"
                            ? "Extraction took too long — try again"
                            : errCode === "empty_content"
                              ? "Empty email body"
                              : errCode.startsWith("quota:")
                                ? "Daily extraction limit reached"
                                : errCode.startsWith("llm_error:")
                                  ? "Extractor hit an error — try again"
                                  : `Failed: ${errCode}`)
              : ok
                ? `${it.reservations_extracted} reservation${it.reservations_extracted !== 1 ? "s" : ""} extracted`
                : it.error === "empty_content"
                  ? "Empty email body"
                  : "0 reservations extracted";
          return (
            <li
              key={it.id}
              className="px-6 py-4 flex items-start gap-4 hover:bg-sand-50 transition-colors"
              data-testid={`inbox-item-${it.email_id}`}
            >
              <div className="mt-1">
                {queued ? (
                  <RefreshCw size={16} className="text-dusk animate-spin" />
                ) : ok ? (
                  <CheckCircle2 size={16} className="text-[#5E8467]" />
                ) : (
                  <AlertCircle size={16} className={errored ? "text-[#B24B3E]" : "text-[#57504A]"} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sand-900 truncate">
                  {it.subject || "(no subject)"}
                </p>
                <p className="text-xs text-sand-500 truncate font-mono">
                  {it.from || "?"} · {fmtTime(it.received_at)}
                </p>
                <p className={`text-xs mt-1 ${errored ? "text-[#B24B3E]" : "text-sand-600"}`} data-testid={`inbox-status-${it.email_id}`}>
                  {errText}
                  {it.reprocessed_at && !queued && !errored && <span className="ml-2 text-sand-400">· reprocessed</span>}
                </p>
                {errored && it.reprocess_error_detail && (
                  <details className="text-[10px] text-sand-500 mt-1 font-mono" data-testid={`inbox-detail-${it.email_id}`}>
                    <summary className="cursor-pointer hover:text-sand-700">Diagnostic detail</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-all bg-sand-50 p-2 rounded max-h-32 overflow-auto">
                      {typeof it.reprocess_error_detail === "string"
                        ? it.reprocess_error_detail
                        : JSON.stringify(it.reprocess_error_detail, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
              <button
                onClick={() => reprocess(it.email_id)}
                disabled={busy === it.email_id || queued}
                className="btn-swiss btn-ghost text-xs disabled:opacity-50"
                data-testid={`inbox-reprocess-${it.email_id}`}
                title="Re-run extraction on this email"
              >
                <RefreshCw size={12} className={`mr-1.5 ${busy === it.email_id || queued ? "animate-spin" : ""}`} />
                {busy === it.email_id ? "..." : queued ? "Working" : "Re-extract"}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
